import { Package, Box, Cylinder, ShieldCheck, Sparkles, Container } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const OPTIONS = [
  { icon: Sparkles, title: "Retail Packs", body: "Consumer-ready 50g, 100g, 200g, 500g and 1kg formats — nitrogen-flushed for extended shelf life and shelf appeal.", weight: "50g – 1kg" },
  { icon: Box, title: "Bulk Packs", body: "Industrial 25kg and 50kg PP woven bags with food-grade LDPE liners — designed for ocean freight and food-manufacturer intake.", weight: "25kg – 50kg" },
  { icon: Package, title: "Stand-up Pouches (SUP)", body: "Premium matte or gloss SUPs with zipper or tin-tie closures — the retail industry standard for spice presentation.", weight: "100g – 500g" },
  { icon: Cylinder, title: "PET Jars", body: "Clear or amber PET jars with induction seals and tamper-evident caps — ideal for gourmet, deli and specialty positioning.", weight: "50g – 200g" },
  { icon: Container, title: "HDPE Containers", body: "Durable HDPE containers with spice-grinder or dispenser tops for foodservice and shaker-format retail lines.", weight: "100g – 500g" },
  { icon: Box, title: "Export Cartons", body: "Corrugated export cartons with individual pouch or jar inners — palletized or floor-loaded per buyer preference.", weight: "5kg – 20kg" },
  { icon: ShieldCheck, title: "Private Label Packaging", body: "Full end-to-end private label — your artwork, dielines, barcodes and destination-market labelling handled in-house.", weight: "Custom" },
  { icon: Sparkles, title: "Custom Packaging", body: "Kraft paper pouches, glass jars, gift-set formats and sustainable mono-material films for premium and eco brands.", weight: "On request" },
];

export default function Packaging() {
  return (
    <>
      <PageHeader
        eyebrow="Packaging Solutions"
        title="Packaging designed for ocean freight and retail shelves."
        italicWord="Packaging"
        subtitle="From heavy-duty 50kg export bags to nitrogen-flushed retail pouches — we build the packaging spec around your product, market and margin."
      />

      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {OPTIONS.map((o, i) => (
              <Reveal key={o.title} delay={i * 60} className="p-8 bg-brand-ivory-warm border border-brand-charcoal/10 hover:border-brand-gold transition-colors group">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 border border-brand-green/50 flex items-center justify-center text-brand-green group-hover:bg-brand-green group-hover:text-brand-ivory transition-colors">
                    <o.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] tracking-[0.28em] uppercase text-brand-gold">{o.weight}</span>
                </div>
                <h3 className="font-serif text-2xl mb-3 text-brand-charcoal">{o.title}</h3>
                <p className="text-brand-charcoal/60 text-[14.5px] leading-relaxed">{o.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTE */}
      <section className="py-24 bg-brand-ivory-warm">
        <div className="container-luxe grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="overline">Sustainability</span>
            <h2 className="font-serif text-4xl mt-4 leading-tight text-brand-charcoal">Recyclable, mono-material and compostable formats.</h2>
            <span className="divider-gold" />
            <p className="text-brand-charcoal/65 leading-relaxed">Increasing numbers of our buyers require sustainable packaging as a purchasing criterion — not a marketing angle. We work with recyclable mono-material laminates, compostable inner liners, glass jars with metal lids and kraft paper pouches to help brands meet destination-market regulations and consumer expectations.</p>
          </Reveal>
          <Reveal delay={100} className="grid grid-cols-2 gap-4 text-[14px]">
            {[
              "Recyclable mono-material films",
              "Compostable inner liners",
              "Food-grade PP with certified LDPE liners",
              "Glass and PET with induction seals",
              "Nitrogen-flushed for shelf life",
              "Kraft paper for eco-brands",
            ].map((s) => (
              <div key={s} className="p-4 bg-brand-ivory border border-brand-charcoal/10 text-brand-charcoal/75">{s}</div>
            ))}
          </Reveal>
        </div>
      </section>
    </>
  );
}
